// eslint-disable-next-line no-unused-vars
import React from 'react';
import { array, bool, func, node, object, string } from 'prop-types';
import { ComboBox, DropdownSkeleton } from '@carbon/react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import OfferStatusBadge from 'Components/Offers/OfferStatusBadge';
import ProductSkeletonItem from 'Components/Products/ProductSkeletonItem';

const StyledOffersMenu = styled.div`
  .offer-selector {
    padding: 15px;

    .cds--list-box__field {
      background: ${ props => props.theme.colors.white };
      border: 1px solid ${ props => props.theme.products.colors.border };
    }
  }

  .offer-option,
  .active-offer {
    align-items: center;
    display: flex;
    justify-content: space-between;
    min-width: 0;
    width: 100%;
  }

  .offer-badges {
    align-items: center;
    display: inline-flex;
    flex-shrink: 0;
    margin-left: 0.5rem;
  }

  .active-offer {
    border-bottom: 1px solid ${ props => props.theme.products.colors.border };
    padding: 0 15px 12px;

    strong {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .productList {
    padding: 0 15px;
  }

  .empty,
  .error {
    font-size: 1rem;
    font-weight: 600;
    padding: 1rem 15px;
  }

  .error {
    color: ${ props => props.theme.colors.red };
  }

  .skeletons {
    padding: 0 15px;
  }

  .offer-footer {
    align-items: center;
    box-shadow: 0 0 8px #00000029;
    display: flex;
    justify-content: center;
    padding: 15.5px 0;

    .seeAll {
      align-items: center;
      border: 1px solid ${ props => props.theme.colors.blue };
      color: ${ props => props.theme.colors.blue };
      display: flex;
      font-size: 1rem;
      font-weight: normal;
      height: 40px;
      justify-content: center;
      max-width: 255px;
      text-align: center;
      width: 100%;
    }
  }
`;

const OffersMenu = ({
  offers,
  selectedOffer,
  activeOffer,
  storage,
  offerUrl,
  loading,
  error,
  children,
  onOfferChange
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const renderOfferItem = item => (
    <span className="offer-option">
      <span>{ item.label }</span>
      <span className="offer-badges">
        <OfferStatusBadge status={ item.expired ? 'expired' : 'valid' } />
        <OfferStatusBadge status={ item.storage } />
      </span>
    </span>
  );

  return (
    <StyledOffersMenu theme={ theme }>
      <div className="offer-selector">
        { loading && !offers.length ? <DropdownSkeleton /> : (
          <ComboBox
            id="miniWishlistOfferSelect"
            titleText={ t('offers.chooseOffer') }
            placeholder={ t('offers.chooseOffer') }
            items={ offers }
            selectedItem={ offers.find(item => item.id === selectedOffer) || null }
            itemToString={ item => item?.label || '' }
            itemToElement={ renderOfferItem }
            disabled={ !offers.length }
            onChange={ onOfferChange }
          />
        ) }
      </div>

      { activeOffer && (
        <div className="active-offer">
          <strong>{ activeOffer.name }</strong>
          <OfferStatusBadge status={ storage } />
        </div>
      ) }

      { loading && (
        <div className="skeletons">
          <ProductSkeletonItem />
          <ProductSkeletonItem />
        </div>
      ) }
      { !loading && error && <p className="error">{ error }</p> }
      { !loading && !error && !offers.length && (
        <p className="empty">{ t('offers.noAvailableOffers') }</p>
      ) }
      { !loading && !error && activeOffer && (
        <>
          { activeOffer.items?.length
            ? <div className="productList">{ children }</div>
            : <p className="empty">{ t('offers.emptyOffer') }</p> }
          <footer className="offer-footer">
            <Link className="seeAll" to={ offerUrl }>
              { t('offers.continueToOffer') } &gt;
            </Link>
          </footer>
        </>
      ) }
    </StyledOffersMenu>
  );
};

OffersMenu.propTypes = {
  offers: array,
  selectedOffer: string,
  activeOffer: object,
  storage: string,
  offerUrl: string,
  loading: bool,
  error: string,
  children: node,
  onOfferChange: func
};

export default OffersMenu;
